import { useState, useEffect, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../config/api';

type TokenState = 'checking' | 'valid' | 'invalid';

export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [tokenState, setTokenState] = useState<TokenState>('checking');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }
    fetch(getApiUrl('/api/auth/validate-reset-token'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setAccountEmail(d.email || '');
          setTokenState('valid');
        } else {
          setTokenState('invalid');
        }
      })
      .catch(() => setTokenState('invalid'));
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/set-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (response.ok) {
        setDone(true);
      } else {
        setError(data.error || 'Failed to set password');
      }
    } catch {
      setError('Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Britannia LeadGen</h1>
          <p className="text-dark-text-secondary">Set your password</p>
        </div>

        <div className="card p-8">
          {tokenState === 'checking' && (
            <p className="text-center text-dark-text-secondary">Checking your link...</p>
          )}

          {tokenState === 'invalid' && (
            <div className="text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-white mb-2">Link invalid or expired</h2>
              <p className="text-dark-text-secondary text-sm mb-6">
                This link is no longer valid. Ask us to send you a new invite,
                or request a password reset from the sign-in page.
              </p>
              <Link to="/login" className="btn btn-primary inline-block">
                Go to Sign In
              </Link>
            </div>
          )}

          {tokenState === 'valid' && done && (
            <div className="text-center">
              <div className="text-4xl mb-4">✅</div>
              <h2 className="text-xl font-bold text-white mb-2">Password set</h2>
              <p className="text-dark-text-secondary text-sm mb-6">
                Your password has been saved. You can now sign in to view your leads.
              </p>
              <Link to="/login" className="btn btn-primary inline-block">
                Sign In
              </Link>
            </div>
          )}

          {tokenState === 'valid' && !done && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {accountEmail && (
                <p className="text-sm text-dark-text-secondary">
                  Setting password for <span className="text-dark-text font-medium">{accountEmail}</span>
                </p>
              )}

              {error && (
                <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-dark-text mb-2">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="input"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-dark-text mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat your password"
                  className="input"
                  required
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" disabled={saving} className="w-full btn btn-primary h-12">
                {saving ? 'Saving...' : 'Set Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
